import type { Story3ExpertId, Story3FeedbackModeId } from '../types';

const API_BASE = import.meta.env.VITE_API_URL ?? '';

export type Story3ApiMode = 'live' | 'mock';

export interface Story3FeedbackResult {
  mode: Story3FeedbackModeId;
  expert?: Story3ExpertId;
  customExpert?: string;
  body: string;
  apiMode: Story3ApiMode;
}

const STORY3_FETCH_MS = 35_000;

export async function fetchStory3Health(): Promise<Story3ApiMode | null> {
  try {
    const res = await fetch(`${API_BASE}/api/health`, {
      credentials: 'include',
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { story3?: Story3ApiMode };
    return data.story3 === 'live' || data.story3 === 'mock'
      ? data.story3
      : null;
  } catch {
    return null;
  }
}

export async function fetchStory3Feedback(body: {
  mode: Story3FeedbackModeId;
  expert?: Story3ExpertId;
  customExpert?: string;
  text: string;
}): Promise<Story3FeedbackResult> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), STORY3_FETCH_MS);

  let res: Response;
  try {
    res = await fetch(`${API_BASE}/api/story3/feedback`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (e) {
    clearTimeout(timer);
    if (e instanceof Error && e.name === 'AbortError') {
      throw new Error('Det tog for lang tid — tjek forbindelsen og prøv igen.');
    }
    throw new Error(
      'Kunne ikke kontakte serveren. Kør med VITE_USE_API=true og start server.',
    );
  } finally {
    clearTimeout(timer);
  }

  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? 'Kunne ikke hente feedback.');
  }

  return (await res.json()) as Story3FeedbackResult;
}
